import { useState, useEffect } from 'react';
import { User } from '../Types/Types';

const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const apiUrl = process.env.REACT_APP_API_URL;
  const url = `${apiUrl}/api`;

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    const encodedSearchQuery = encodeURIComponent(searchQuery);
    const fullUrl = `${url}/users?search=${encodedSearchQuery}&sortBy=${sortBy}&sortOrder=${sortOrder}`;

    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users');
      }

      const data: User[] = await response.json();
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  };

  // Create user
const createUser = async (data: { name: string; email: string; password: string; role?: string }) => {
  setLoading(true);
  setError(null);

  const token = localStorage.getItem('token');

  try {
    const response = await fetch(`${url}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create user');
    }

    await fetchUsers();
  } catch (err: any) {
    setError(err.message || 'An error occurred while creating user');
  } finally {
    setLoading(false);
  }
};

  // Update user
const updateUser = async (id: string, data: { name?: string; email?: string; password?: string; role?: string }) => {
  setLoading(true);
  setError(null);

  const token = localStorage.getItem('token');

  try {
    const response = await fetch(`${url}/users/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update user');
    }

    await fetchUsers();
  } catch (err: any) {
    setError(err.message || 'An error occurred while updating user');
  } finally {
    setLoading(false);
  }
};

  // Delete user
  const deleteUser = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${url}/users/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete user');
      }

      setUsers(prev => prev.filter(user => user._id !== id));
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting user');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, sortBy, sortOrder]);

  return {
    users,
    loading,
    error,
    createUser,
    updateUser,
    deleteUser,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
  };
};

export default useUsers;
